/**
 * Camera Presets for Director Panel
 * ==================================
 * 15 presets across 4 categories. Each preset has one canonical prompt text
 * that the enhancement LLM adapts to model-specific phrasing.
 */

export const CAMERA_CATEGORIES = [
  { key: 'movement', label: 'Movement' },
  { key: 'rotation', label: 'Rotation' },
  { key: 'zoom', label: 'Zoom' },
  { key: 'specialty', label: 'Specialty' },
];

export const CAMERA_PRESETS = [
  // ── Movement ──
  { key: 'dolly_in', label: 'Dolly In', category: 'movement', promptText: 'smooth dolly-in toward the subject' },
  { key: 'dolly_out', label: 'Dolly Out', category: 'movement', promptText: 'slow dolly-out pulling away from the subject' },
  { key: 'truck_left', label: 'Truck Left', category: 'movement', promptText: 'lateral truck left, camera gliding sideways' },
  { key: 'truck_right', label: 'Truck Right', category: 'movement', promptText: 'lateral truck right, camera gliding sideways' },
  { key: 'crane_up', label: 'Crane Up', category: 'movement', promptText: 'crane shot rising upward to reveal the scene from above' },
  { key: 'crane_down', label: 'Crane Down', category: 'movement', promptText: 'crane shot descending from above toward the subject' },

  // ── Rotation ──
  { key: 'pan_left', label: 'Pan Left', category: 'rotation', promptText: 'gentle pan left across the scene' },
  { key: 'pan_right', label: 'Pan Right', category: 'rotation', promptText: 'gentle pan right across the scene' },
  { key: 'tilt_up', label: 'Tilt Up', category: 'rotation', promptText: 'slow tilt up from ground level toward the sky' },
  { key: 'tilt_down', label: 'Tilt Down', category: 'rotation', promptText: 'slow tilt down from above toward the ground' },
  { key: 'arc_left', label: 'Arc Left', category: 'rotation', promptText: 'arc shot circling left around the subject' },
  { key: 'orbit', label: 'Orbit 360', category: 'rotation', promptText: '360-degree orbit around the subject' },

  // ── Zoom ──
  { key: 'zoom_in', label: 'Zoom In', category: 'zoom', promptText: 'slow zoom in toward the subject' },
  { key: 'zoom_out', label: 'Zoom Out', category: 'zoom', promptText: 'slow zoom out revealing the full scene' },
  { key: 'crash_zoom', label: 'Crash Zoom', category: 'zoom', promptText: 'rapid crash zoom in toward the subject for dramatic impact' },

  // ── Specialty ──
  { key: 'handheld', label: 'Handheld', category: 'specialty', promptText: 'handheld camera with subtle organic movement and natural shake' },
  { key: 'tracking', label: 'Tracking Shot', category: 'specialty', promptText: 'tracking shot following the subject as they move through the scene' },
  { key: 'static', label: 'Static', category: 'specialty', promptText: 'locked-off static camera, no movement' },
];

/**
 * Get a camera preset by key.
 * @param {string} key
 * @returns {Object|null}
 */
export function getCameraPreset(key) {
  if (!key || key === 'none') return null;
  return CAMERA_PRESETS.find(p => p.key === key) || null;
}

/**
 * Get prompt text for a camera preset. Returns empty string for 'none'.
 * @param {string} presetKey
 * @returns {string}
 */
export function getCameraPromptText(presetKey) {
  const preset = getCameraPreset(presetKey);
  return preset?.promptText || '';
}

/**
 * Get presets grouped by category.
 * @returns {Array<{ category: Object, presets: Object[] }>}
 */
export function getGroupedCameraPresets() {
  return CAMERA_CATEGORIES.map(cat => ({
    category: cat,
    presets: CAMERA_PRESETS.filter(p => p.category === cat.key),
  }));
}
